import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { STATUS, useJoyride, type EventData, type Step } from 'react-joyride';
import { useTheme } from '@mui/material';
import { useDemo } from '../../contexts/DemoContext';
import { DEMO_TOUR, DEMO_TOUR_FINALE } from '../../demo/tour.config';
import { PERSONA_BAR_HEIGHT } from './PersonaBar';

/**
 * Drives the guided tour.
 *
 * Two things make this more than a list of tooltips.
 *
 * First, each step may need a different persona (an async token swap) and a different route before
 * its target exists. Joyride awaits a step's `before` hook, so that preparation belongs to the step
 * itself and the tour simply waits for it.
 *
 * Second, the visitor is not locked into the running order. If they click a persona tab mid-tour,
 * the tour follows them to the first step of that persona rather than dragging them back to where
 * it had got to. Someone who jumps to the Parent tab wants to hear about the parent view.
 *
 * Steps without a target are anchored to the page centre, and `targetWaitTimeout` gives a
 * late-mounting target a few seconds before the tour moves on. A sales demo that points a tooltip
 * at nothing, or stalls, is worse than one that narrates the screen.
 */

/**
 * Set only when a visitor explicitly asks to be walked through, and consumed the moment the tour
 * starts.
 *
 * This was previously an "already seen" flag whose *absence* started the tour. That made the tour opt-out
 * and, worse, re-armed it on every page load: the session resumes silently from localStorage, the
 * tour saw no seen-flag and started itself, and its first step navigated away from the landing
 * page. A visitor who returned to the homepage was pulled straight back into the app and could not
 * get out. Requiring a positive, single-use request means a page load can never start a tour on
 * its own.
 */
const TOUR_REQUEST_KEY = 'mwl_demo_tour_requested';

/** First step belonging to a persona, so a manual switch lands at the start of that section. */
function firstStepOfPersona(persona: string): number {
  const index = DEMO_TOUR.findIndex(step => step.persona === persona);

  return index === -1 ? 0 : index;
}

/**
 * Poll for a selector to mount, bounded so a target that never appears can't hang the tour.
 *
 * `navigate()` returns as soon as the history entry changes — it does not wait for the resulting
 * route to render, let alone for that page's own data fetch to resolve and mount the element the
 * step is about to spotlight. Without this wait, joyride looks for the target immediately after
 * `before()` resolves, finds nothing, and silently skips the step. Capped well under
 * `beforeTimeout` so a step whose target genuinely never appears still falls through to joyride's
 * own (slower, retried) lookup and centred fallback rather than hanging here.
 */
function waitForElement(selector: string, timeoutMs: number): Promise<void> {
  return new Promise(resolve => {
    const deadline = Date.now() + timeoutMs;

    const check = () => {
      if (document.querySelector(selector) || Date.now() >= deadline) {
        resolve();
        return;
      }

      window.setTimeout(check, 100);
    };

    check();
  });
}

export default function DemoTour() {
  const { enabled, session, activeRole, switchPersona } = useDemo();
  const navigate = useNavigate();
  const theme = useTheme();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  /**
   * True while the tour is performing its own persona switch inside a `before` hook.
   *
   * Without it, the tour's own switches would look identical to a visitor clicking a tab and the
   * tour would jump backwards to the start of the section it was already entering.
   */
  const switchingForTour = useRef(false);

  const finish = useCallback(() => {
    // Clear rather than set: the request was already consumed at start, and leaving nothing behind
    // means finishing and skipping are the same state — no tour pending.
    window.localStorage.removeItem(TOUR_REQUEST_KEY);
    setRun(false);
  }, []);

  const steps: Step[] = useMemo(() => {
    const tourSteps: Step[] = DEMO_TOUR.map(step => ({
      target: step.target ?? 'body',
      placement: step.target ? 'auto' : 'center',
      title: step.title,
      content: step.body,
      // Awaited by the tour: the persona swap, the navigation, and the target's own mount all
      // complete before the step is shown. Runs on back-navigation too, so stepping backwards
      // restores the right context.
      before: async () => {
        let switched = true;

        if (step.persona !== activeRole) {
          switchingForTour.current = true;

          try {
            switched = await switchPersona(step.persona);
          } finally {
            switchingForTour.current = false;
          }
        }

        // switchPersona never rejects — a failure sets `error` and resolves false. Navigating
        // anyway would send a visitor who isn't actually that persona into a route guarded for it,
        // which redirects to /login. Staying put and letting the target-wait time out is the
        // existing graceful-degradation path (see the file comment above); a login form is not.
        if (switched && window.location.pathname !== step.route) {
          navigate(step.route);
        }

        if (step.target) {
          await waitForElement(step.target, 4000);
        }
      },
      beforeTimeout: 10000,
    }));

    return [
      ...tourSteps,
      {
        target: DEMO_TOUR_FINALE.target,
        placement: 'bottom',
        title: DEMO_TOUR_FINALE.title,
        content: DEMO_TOUR_FINALE.body,
      },
    ];
  }, [activeRole, switchPersona, navigate]);

  const handleEvent = useCallback(
    (data: EventData) => {
      setStepIndex(data.index);

      if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) {
        finish();
      }
    },
    [finish]
  );

  const { Tour, controls } = useJoyride({
    steps,
    run,
    continuous: true,
    onEvent: handleEvent,
    options: {
      primaryColor: theme.palette.primary.main,
      textColor: theme.palette.text.primary,
      arrowColor: theme.palette.background.paper,
      backgroundColor: theme.palette.background.paper,
      overlayColor: 'rgba(0, 0, 0, 0.55)',
      // Skip is always offered — a prospect who wants to poke around should never feel trapped.
      buttons: ['back', 'skip', 'primary'],
      skipBeacon: true,
      // Clicking the backdrop should not end the tour by accident mid-sentence.
      overlayClickAction: false,
      // A target that never appears must not strand the visitor; the tour moves on.
      targetWaitTimeout: 5000,
      spotlightPadding: 6,
      // joyride auto-scrolls a step's target to just below the viewport top. The PersonaBar is
      // sticky there and would otherwise cover the very thing being spotlighted; leave room for it
      // (plus joyride's own default breathing room) before deciding the target is in view.
      scrollOffset: PERSONA_BAR_HEIGHT + 20,
      // joyride's own default (100) sits under MUI's drawer (1200) and appBar (1100), and under
      // the PersonaBar itself (drawer + 2 = 1202) — the tooltip and overlay rendered behind the
      // app's chrome instead of above it. theme.zIndex.modal clears all three.
      zIndex: theme.zIndex.modal,
    },
    locale: {
      back: 'Back',
      close: 'Close',
      last: 'Start exploring',
      next: 'Next',
      skip: 'Skip the tour',
    },
    styles: {
      tooltip: { borderRadius: 10, width: 420, maxWidth: '92vw' },
      tooltipTitle: { fontSize: '1.1rem', fontWeight: 700, textAlign: 'left' },
      tooltipContent: { textAlign: 'left', lineHeight: 1.6 },
      buttonPrimary: { textTransform: 'none', fontWeight: 600 },
      buttonBack: { textTransform: 'none' },
      buttonSkip: { textTransform: 'none' },
    },
  });

  // Start only if this visitor asked for the tour, and consume the request as we do. Consuming it
  // is what keeps a reload mid-tour from restarting the whole thing and dragging the visitor back
  // through routes they were trying to leave.
  useEffect(() => {
    if (!enabled || !session || run) {
      return;
    }

    if (window.localStorage.getItem(TOUR_REQUEST_KEY) === 'true') {
      window.localStorage.removeItem(TOUR_REQUEST_KEY);
      setRun(true);
    }
  }, [enabled, session, run]);

  // Follow a visitor who changes persona themselves.
  useEffect(() => {
    if (!run || !activeRole || switchingForTour.current) {
      return;
    }

    const current = DEMO_TOUR[stepIndex];

    // The finale step (past the end of DEMO_TOUR) belongs to no persona; leave it alone.
    if (!current || current.persona === activeRole) {
      return;
    }

    controls.go(firstStepOfPersona(activeRole));
  }, [activeRole, run, stepIndex, controls]);

  if (!enabled || !session || !run) {
    return null;
  }

  return <>{Tour}</>;
}

/** Ask for the tour on the next entry into the sandbox. Consumed once it starts. */
export function requestDemoTour(): void {
  window.localStorage.setItem(TOUR_REQUEST_KEY, 'true');
}

/**
 * Withdraw any outstanding request, for a visitor who chose a persona rather than asking to be
 * walked through — and for anything else that moves them somewhere they picked themselves.
 */
export function cancelDemoTour(): void {
  window.localStorage.removeItem(TOUR_REQUEST_KEY);
}
